import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { LeaveGuard } from '../../../../shared/sso-workspace/leave-guard'
import { useIsMobile } from '../../../../shared/hooks/use-is-mobile'
import { serverMessage } from '../../../../shared/sso-workspace/format'
import { createRoute, getRoute, updateRoute } from './alert-routing-api'
import { TriggerStep, FiltersStep, WhenStep, ActionsStep, TestStep, pendingSecret } from './editor-steps'
import { useAlertRoutingAccess } from './access'
import { useCatalog } from './catalog'
import { categoryNoun } from './route-summary'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useAuditRecord } from '../../../../shared/audit/history/audit-record-context'
import './alert-routing.scss'

const STEPS = [['Trigger', TriggerStep], ['Filters', FiltersStep], ['When', WhenStep], ['Actions', ActionsStep], ['Test', TestStep]]
const empty = category => ({ name: '', category: category || 'errors', triggerKinds: [], minSeverity: 'info', filters: [], schedule: null,
  deliveryMode: 'immediate', digestMinutes: 0, actions: [], scopeType: 'platform', organizationID: null, choosing: false })

// What stops a save. "Only these alerts" with nothing ticked is never read
// as "every alert": the server would take empty triggerKinds as the wildcard.
export const routeProblems = (r, catalog) => {
  const out = []
  if (r.choosing && r.triggerKinds.length === 0) out.push(`Tick at least one alert, or choose every ${categoryNoun(r.category, catalog)} alert.`)
  return out
}

// toBody is exactly what POST/PUT /routes accepts.
export const toBody = r => ({
  name: r.name.trim(), scopeType: r.scopeType, organizationID: r.organizationID, category: r.category, triggerKinds: r.triggerKinds,
  minSeverity: r.minSeverity, filters: r.filters, schedule: r.schedule, deliveryMode: r.deliveryMode,
  digestMinutes: r.deliveryMode === 'digest' ? r.digestMinutes : 0,
  actions: r.actions.filter(a => a.targetID).map(a => ({ targetID: a.targetID, enabled: a.enabled !== false, mapping: a.mapping || {} }))
})

export const RouteEditor = () => {
  const { routeID } = useParams()
  const { search } = useLocation()
  const history = useHistory()
  const params = new URLSearchParams(search)
  const mobile = useIsMobile()
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const isNew = routeID === 'new'
  const [route, setRoute] = useState(isNew ? empty(params.get('category')) : null)
  const [saved, setSaved] = useState(null)
  const [step, setStep] = useState(Math.min(Math.max(Number(params.get('step') || 1), 1), 5) - 1)
  const [error, failure, setError] = useErrorLine()
  // The id a create just returned: the URL moves to it, and the route in
  // hand is already what the server stored, so it is not fetched again.
  const created = useRef(null)

  useEffect(() => {
    if (isNew || created.current === routeID) return
    getRoute(routeID).then(r => { const v = { ...empty(r.category), ...r, triggerKinds: r.triggerKinds || [], actions: r.actions || [], filters: r.filters || [], choosing: (r.triggerKinds || []).length > 0 }; setRoute(v); setSaved(v) },
      err => setError(serverMessage(err, "This route couldn't be loaded."), err))
  }, [routeID, isNew, setError])

  // History: whose route this is, once loaded (null: a platform route), for
  // a reader without SYS_AUDIT_LOG (record-routes.js recordOrg)
  useAuditRecord({ type: 'alert_route', id: !isNew && saved ? routeID : undefined, organizationID: !isNew && saved ? (saved.organizationID ?? null) : undefined })

  const body = useMemo(() => (route ? toBody(route) : null), [route])
  const dirty = !saved || JSON.stringify(toBody(saved)) !== JSON.stringify(body)
  const set = patch => setRoute(r => ({ ...r, ...patch }))

  const problems = route ? routeProblems(route, catalog) : []

  const save = async () => {
    if (!canEdit || pendingSecret(route)) return
    if (problems.length) { setError(problems[0]); return }
    try {
      setError(null)
      const out = isNew ? await createRoute(body) : await updateRoute(routeID, body)
      setSaved({ ...route })
      if (isNew) created.current = String(out.id)
      if (isNew) history.replace(`/admin/alert-routing/routes/${out.id}?step=${step + 1}`)
    } catch (err) {
      setError(serverMessage(err, "The route couldn't be saved."), err)
    }
  }

  if (isNew && !canEdit) {
    return (
      <FleetPage title="New alert route">
        <Notice tone="warn">{reason}</Notice>
        <button type="button" className="btn sw-btn" onClick={() => history.push('/admin/alert-routing?tab=routes')}>Back to routes</button>
      </FleetPage>
    )
  }
  if (!route) return error ? <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice> : <p className="sw-hint">Loading…</p>
  const readOnly = !canEdit
  // A generated secret not yet confirmed holds the editor on this step.
  const held = pendingSecret(route)
  const stepProps = { route, set, body, savedID: isNew ? null : Number(routeID), dirty, readOnly, problems }
  // Read-only: every step's inputs are disabled; the Test step stays usable
  // for Preview (which never writes) and offers no Send test.
  const renderStep = (Step, i) => (readOnly && i < 4
    ? <fieldset disabled className="ar-readonly"><Step {...stepProps} /></fieldset>
    : <Step {...stepProps} />)
  return (
    <FleetPage title={isNew ? 'New alert route' : `Route: ${route.name}`} purpose="Pick alerts, filter them, choose when, and choose where they go.">
      {readOnly && <Notice tone="warn">{reason}</Notice>}
      <label className="sw-label" htmlFor="ar-name">Route name</label>
      <input id="ar-name" className="form-control mb-3" maxLength={120} value={route.name} readOnly={readOnly} onChange={e => set({ name: e.target.value })} />
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      <LeaveGuard when={held} title="Copy the secret first" label="Copy the secret first" leaveLabel="Leave and lose the secret"
        message="The new target's secret is shown only once. If you leave now, it can't be shown again and the target will need a new secret." />
      {held && <Notice tone="warn">Copy the new target's secret and choose "I've copied it" before you go on.</Notice>}
      {mobile ? (
        STEPS.map(([label, Step], i) => <section key={label} className="ar-step"><h2 className="h5">{i + 1}. {label}</h2>{renderStep(Step, i)}</section>)
      ) : (
        <>
          <nav className="sw-tabs ar-stepper" aria-label="Steps">
            {STEPS.map(([label], i) => <button key={label} type="button" className={i === step ? 'active' : undefined} aria-current={i === step ? 'step' : undefined} disabled={held} onClick={() => setStep(i)}>{i + 1}. {label}</button>)}
          </nav>
          <section className="ar-step"><h2 className="h5">{step + 1}. {STEPS[step][0]}</h2>{renderStep(STEPS[step][1], step)}</section>
          <div className="ar-row">
            {step > 0 && <button type="button" className="btn sw-btn" disabled={held} onClick={() => setStep(step - 1)}>Back</button>}
            {step < 4 && <button type="button" className="btn sw-btn" disabled={held} onClick={() => setStep(step + 1)}>Next</button>}
          </div>
        </>
      )}
      <div className="ar-row mt-3">
        {canEdit && <button type="button" className="btn sw-btn-primary" onClick={save} disabled={!route.name.trim() || held}>Save route</button>}
        <button type="button" className="btn sw-btn" disabled={held} onClick={() => history.push(`/admin/alert-routing?tab=routes&category=${route.category}`)}>Back to routes</button>
      </div>
    </FleetPage>
  )
}
